import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { base_url } from "../baseUrl";

/**
 * useOrganisationUsers
 *
 * UI-facing hook for customer-managed user administration.
 *
 * Each user object structure (as used by the UI):
 * {
 *   _id: string;
 *   name?: string;
 *   email: string;
 *   role: string; // e.g. "Super Admin", "Customer Admin", "Standard User", "View Only"
 *   canCreateSalesOrders?: boolean;
 *   canViewPrices?: boolean;
 *   isActive?: boolean;
 * }
 */

// Get authentication headers using token stored in localStorage
const getHeaders = () => {
  const storedUser = localStorage.getItem("user");
  const token = storedUser ? JSON.parse(storedUser).token : null;

  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
};

const useOrganisationUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Call backend API to get organisation users
      const { data } = await axios.get(
        `${base_url}api/org-users`,
        getHeaders()
      );

      // API response shape:
      // {
      //   owner: {...},
      //   organizationUsers: [...],
      //   organizationUserCount,
      //   totalCount,
      //   message
      // }

      const rawUsers = [
        ...(data?.owner ? [data.owner] : []),
        ...(Array.isArray(data?.organizationUsers)
          ? data.organizationUsers
          : []),
      ];

      // Normalise into the shape expected by the UI
      const normalisedUsers = rawUsers.map((u) => {
        const roleName =
          typeof u.role === "object" && u.role !== null
            ? u.role.name || u.role._id || ""
            : u.role || "";

        const canCreateSalesOrders =
          u.canCreateSalesOrders ??
          u.role?.permissions?.createSalesOrders ??
          false;

        const canViewPrices =
          u.canViewPrices ?? u.role?.permissions?.viewPrices ?? false;

        const isActive =
          u.isActive ?? u.isAccessEnabled ?? true;

        return {
          ...u,
          name: u.name || u.CustomerName || "",
          role: roleName,
          canCreateSalesOrders,
          canViewPrices,
          isActive,
        };
      });

      setUsers(normalisedUsers);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch organisation users"
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Lazy load when a UI screen first mounts this hook
    fetchUsers();
  }, [fetchUsers]);

  const addUser = async (payload) => {
    setSaving(true);
    setError(null);

    // For now this only updates local state; the actual creation
    // is done via the separate useCreateOrgUser hook.
    // Note: This creates a temporary ID. After API creation, 
    // use fetchUsers() to get the real user with MongoDB ObjectId.
    const newUser = {
      _id: `user-${Date.now()}`,
      name: payload.name,
      email: payload.email,
      role: payload.role || "Standard User",
      canCreateSalesOrders: payload.canCreateSalesOrders || false,
      canViewPrices:
        payload.canViewPrices !== undefined ? payload.canViewPrices : true,
      isActive: payload.isActive !== undefined ? payload.isActive : true,
    };

    setUsers((prev) => [...prev, newUser]);
    setSaving(false);
    return newUser;
  };

  const updateUser = async (id, updates) => {
    setSaving(true);
    setError(null);

    try {
      // Update organisation user (e.g. role, access flags)
      const { data } = await axios.put(
        `${base_url}api/org-users/${id}`,
        updates,
        getHeaders()
      );

      const updatedUserRaw = data?.user || data;

      // Normalise backend response into the shape expected by the UI
      const roleName =
        typeof updatedUserRaw?.role === "object" && updatedUserRaw?.role !== null
          ? updatedUserRaw.role.name || updatedUserRaw.role._id || ""
          : updatedUserRaw?.role || "";

      const updatedUser = {
        ...updatedUserRaw,
        role: roleName,
        isActive: updatedUserRaw?.isActive ?? updatedUserRaw?.isAccessEnabled,
      };

      setUsers((prev) =>
        prev.map((u) => (u._id === id ? { ...u, ...updatedUser } : u))
      );

      return updatedUser;
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to update organisation user"
      );
      return null;
    } finally {
      setSaving(false);
    }
  };

  const removeUser = async (id) => {
    setSaving(true);
    setError(null);

    try {
      await axios.delete(`${base_url}api/org-users/${id}`, getHeaders());
      setUsers((prev) => prev.filter((u) => u._id !== id));
      return true;
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to delete organisation user"
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    users,
    loading,
    saving,
    error,
    fetchUsers,
    addUser,
    updateUser,
    removeUser,
  };
};

export default useOrganisationUsers;

